import type { DiveLocation } from '../services/userDataService';
import { getDistanceKm } from './geoDistance';

/** A dive within this distance of a saved location inherits that location. */
export const NEARBY_LOCATION_RADIUS_KM = 0.25;

const updatedAtMs = (location: DiveLocation): number => {
  const time = new Date(location.updatedAt).getTime();
  return Number.isNaN(time) ? 0 : time;
};

/**
 * Picks the saved location a dive at (latitude, longitude) should inherit:
 * the nearest user-edited one within the radius, else the nearest of any kind.
 * Ties on distance go to the most recently updated.
 */
export const findNearbyLocation = (
  locations: Iterable<DiveLocation>,
  latitude: number,
  longitude: number,
  radiusKm: number = NEARBY_LOCATION_RADIUS_KM
): DiveLocation | undefined => {
  let bestUserEdited: { location: DiveLocation; distanceKm: number } | undefined;
  let bestAny: { location: DiveLocation; distanceKm: number } | undefined;

  const isBetter = (candidate: { location: DiveLocation; distanceKm: number }, current?: { location: DiveLocation; distanceKm: number }) =>
    !current ||
    candidate.distanceKm < current.distanceKm ||
    (candidate.distanceKm === current.distanceKm && updatedAtMs(candidate.location) > updatedAtMs(current.location));

  for (const location of Array.from(locations)) {
    const distanceKm = getDistanceKm(latitude, longitude, location.latitude, location.longitude);
    if (!(distanceKm <= radiusKm)) continue;

    const candidate = { location, distanceKm };
    if (isBetter(candidate, bestAny)) bestAny = candidate;
    if (location.isUserEdited === true && isBetter(candidate, bestUserEdited)) bestUserEdited = candidate;
  }

  return (bestUserEdited ?? bestAny)?.location;
};
